import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { size, uniqBy } from 'lodash';
import { VictoryChart, VictoryAxis, VictoryBar, Line, VictoryLabel } from 'victory';

import {
    getDomainForDay,
    getBeginDate,
    getEndDate,
    getStatusFillColor,
    getStatusStrokeColor,
} from './Timeline.helpers';

import { timelineChartAxisX } from './TimelineChart.parts';
import { chartTheme, chartLabelStyles, getGridStroke } from '../../common/ChartTheme';
import { TimelineInfoTooltipContent } from '../Tooltip/TimelineInfoTooltipContent';
import { RoundedBottomBorder, RoundedBottomBorderNoData } from '../_charts/Chart.styles';
import { barCornerRadius } from '../_charts/Chart.utils';
import { defaultVictoryContainer } from '../_charts/Chart.defaults';
import { BarWithTooltip } from './BarWithTooltip';

export const TimelineChart = ({
    timelineData,
    turbineData,
    chartWidth,
    itemLabelWidth,
    tickLabelComponent,
    chartDay,
    theme,
    message,
}) => {
    // To keep row height consistenly at (rowHeight), we have to calculate chart height
    const rowHeight = 35;
    const itemHeight = 15;
    const timeAxisHeight = 35;
    const bordersFix = 2;

    const chartPadding = {
        left: itemLabelWidth - bordersFix / 2,
        right: -bordersFix,
        top: timeAxisHeight,
        bottom: timeAxisHeight,
    };

    if (!timelineData) {
        return <RoundedBottomBorderNoData chartWidth={chartWidth} />;
    }

    if (timelineData.length === 0) {
        return <RoundedBottomBorderNoData chartWidth={chartWidth} message={message || 'No data for selected period'} />;
    }

    const timelineRows = size(uniqBy(turbineData, 'key'));

    const internalChartDay = timelineData[0] ? moment(timelineData[0].beginDate).toDate() : moment().toDate();

    const calculatedChartHeight = rowHeight * timelineRows + chartPadding.top + chartPadding.bottom;

    return (
        <RoundedBottomBorder>
            <VictoryChart
                theme={chartTheme}
                domainPadding={{ x: [-rowHeight / 2, rowHeight / 2] }}
                height={calculatedChartHeight}
                width={chartWidth - bordersFix}
                padding={chartPadding}
                domain={{
                    y: getDomainForDay(chartDay || internalChartDay),
                    x: [0, timelineRows],
                }}
                containerComponent={defaultVictoryContainer}>
                <VictoryAxis
                    key="1"
                    name="entity-axis"
                    orientation="left"
                    tickLabelComponent={tickLabelComponent || <VictoryLabel dx={-50} />}
                    gridComponent={<Line x1={0} />}
                    fixLabelOverlap
                    style={{
                        tickLabels: chartLabelStyles,
                        grid: {
                            strokeWidth: rowHeight + 1,
                            stroke: getGridStroke(theme),
                        },
                    }}
                />
                {timelineChartAxisX({
                    orientation: 'bottom',
                    timeAxisHeight,
                    chartDay: chartDay || internalChartDay,
                })}
                {timelineChartAxisX({
                    orientation: 'top',
                    timeAxisHeight,
                    chartDay: chartDay || internalChartDay,
                })}
                {timelineData.length > 0 && (
                    <VictoryBar
                        horizontal
                        key="2"
                        name="performance-chart"
                        cornerRadius={barCornerRadius}
                        dataComponent={<BarWithTooltip content={TimelineInfoTooltipContent} />}
                        style={{
                            data: {
                                width: itemHeight,
                                fill: ({ datum }) => getStatusFillColor(datum.type),
                                stroke: ({ datum }) => getStatusStrokeColor(datum.type),
                                strokeWidth: 1,
                                fillOpacity: 0.75,
                            },
                        }}
                        x={(d) => d.key}
                        y={getBeginDate}
                        y0={getEndDate}
                        data={timelineData}
                    />
                )}
            </VictoryChart>
        </RoundedBottomBorder>
    );
};

TimelineChart.displayName = 'TimelineChart';

TimelineChart.propTypes = {
    timelineData: PropTypes.array,
    turbineData: PropTypes.array,
    chartWidth: PropTypes.number,
    itemLabelWidth: PropTypes.number,
    tickLabelComponent: PropTypes.object,
    chartDay: PropTypes.object,
};

TimelineChart.defaultProps = {
    chartWidth: 1890,
    itemLabelWidth: 400,
};
